/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback} from 'react'
import clsx from 'clsx'
import {XMarkIcon} from '@heroicons/react/24/outline'
import {closeModal} from 'helpers/actions'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import {ModalSize} from 'types/enums/ModalSize'

import Headbar from './Headbar'
import Sidebar from './Sidebar'

interface LayoutProps {
    children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const dispatch = useAppDispatch()
    const popup =
        useTypedSelector((state) => state.ui?.modal) ??
        {
            isOpen: false,
            component: null,
            props: {},
        }

    const closeModalHandler = useCallback(() => {
        const closeModalCallback = popup.props?.closeModal
        if (closeModalCallback) {
            closeModalCallback()
            return
        }
        dispatch(closeModal())
    }, [dispatch, popup.props?.closeModal])

    const isOpen = popup.isOpen || false
    const isCloseEnabled = popup.props?.isCloseEnabled ?? true
    const popupSize = popup.props?.modalSize || ModalSize.FullScreen
    const isRoundedFullscreen = isOpen && popupSize === ModalSize.RoundedFullscreen

    return (
        <div
            className={clsx(
                'Layout layout-root w-full h-screen min-h-0 overflow-hidden bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-50'
            )}
        >
            {/* Permanent sidebar */}
            <Sidebar />

            {/* Main area */}
            <div className={clsx('layout-main flex flex-col h-full min-h-0 overflow-hidden')}>
                <Headbar />
                <main
                    className={clsx(
                        'layout-content flex-1 h-full min-h-0 overflow-hidden shadow-hover relative inner-shadow bg-gray-100 dark:bg-gray-900'
                    )}
                >
                    <div className={'bg-gray-100 dark:bg-gray-900 pl-2 pt-2 md:pl-3 md:pt-3 2xl:pl-4 2xl:pt-4 3xl:pl-6 3xl:pt-6 inner-shadow w-full h-full min-h-0 overflow-hidden'}>
                        <section
                            className={clsx(
                                'layout-inner h-full min-h-0 overflow-hidden flex flex-col border  border-solid dark:border-gray-700 dark:border-opacity-20 border-gray-200/80',
                                isRoundedFullscreen
                                    ? 'rounded-tl-2xl bg-white dark:bg-gray-950 overflow-hidden'
                                    : 'bg-white dark:bg-gray-950'
                            )}
                        >
                            {isRoundedFullscreen ? (
                                <div className={clsx('RoundedFullscreenModal relative w-full h-full overflow-hidden')}>
                                    {isCloseEnabled && (
                                        <button
                                            type="button"
                                            className={clsx(
                                                'absolute right-4 top-3 z-100 size-10 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-colors rounded-md'
                                            )}
                                            onClick={closeModalHandler}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className={clsx('w-4 h-4 text-white')} aria-hidden="true" />
                                        </button>
                                    )}
                                    <div className={clsx('w-full h-full overflow-y-auto custom-scrollbar')}>
                                        {React.isValidElement(popup.component) ? popup.component : null}
                                    </div>
                                </div>
                            ) : (
                                children
                            )}
                        </section>
                    </div>
                </main>
            </div>
        </div>
    )
}

export default Layout
